// src/pages/AdminUsers.js
import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import { db, functions } from "../firebase";
import {
  collection,
  doc,
  getDocs,
  query,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { useNavigate } from "react-router-dom";

const COPPER = "#B87333";

function normLower(v) {
  return String(v || "").trim().toLowerCase();
}

function computeSearchFields(user) {
  const name = String(user?.name || user?.displayName || "").trim();
  const email = String(user?.email || "").trim();

  return {
    nameLower: normLower(name || email),
    emailLower: normLower(email),
    searchName: String(name || email || "").trim(),
  };
}

function initialsFromName(nameOrEmail) {
  const s = String(nameOrEmail || "").trim();
  if (!s) return "—";

  if (s.includes("@")) {
    const left = s.split("@")[0] || "";
    const a = (left[0] || "").toUpperCase();
    const b = (left[1] || "").toUpperCase();
    return `${a}${b}`.trim() || "—";
  }

  const parts = s.split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] : "";
  const out = (first + last).toUpperCase();
  return out || "—";
}

function formatDateShort(value) {
  if (!value) return "—";

  try {
    if (typeof value?.toDate === "function") {
      return value.toDate().toLocaleDateString();
    }

    if (typeof value?.seconds === "number") {
      return new Date(value.seconds * 1000).toLocaleDateString();
    }

    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString();
    }

    return "—";
  } catch {
    return "—";
  }
}

function getJoinedAt(user) {
  return user?.approvedAt || user?.joinedAt || user?.createdAt || null;
}

function isDisabled(user) {
  const s = normLower(user?.status);
  return s === "disabled" || s === "suspended";
}

function isEmailVerified(user) {
  return user?.emailVerified === true;
}

function isPhoneVerified(user) {
  return user?.phoneVerified === true;
}

function getTimestampMs(value) {
  if (!value) return 0;

  try {
    if (typeof value?.toMillis === "function") {
      return value.toMillis();
    }

    if (typeof value?.seconds === "number") {
      return value.seconds * 1000;
    }

    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) {
      return d.getTime();
    }

    return 0;
  } catch {
    return 0;
  }
}

function buildTournamentHistoryStatus(entrant) {
  if (!entrant) return "—";

  if (entrant.winner) return "Winner";
  if (Number(entrant.finishPlace || 0) > 0) return `Finished ${entrant.finishPlace}`;
  if (entrant.eliminated) return "Eliminated";
  return "Registered";
}

function Avatar({ user, size = 52 }) {
  const name = user?.name || user?.displayName || "";
  const email = user?.email || "";
  const photoUrl = user?.profilePhotoUrl || "";

  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={name ? `${name} avatar` : "avatar"}
        className="rounded-full object-cover border border-zinc-700 bg-zinc-800"
        style={{ width: size, height: size }}
        loading="lazy"
      />
    );
  }

  const initials = initialsFromName(name || email);

  return (
    <div
      className="rounded-full flex items-center justify-center border bg-zinc-950"
      style={{
        width: size,
        height: size,
        borderColor: "rgba(184,115,51,0.25)",
        background: "rgba(184,115,51,0.10)",
        color: COPPER,
        fontWeight: 800,
        letterSpacing: "0.02em",
      }}
      title={name || email || "User"}
      aria-hidden="true"
    >
      {initials}
    </div>
  );
}

function Badge({ text, tone = "neutral" }) {
  const tones = {
    copper:
      "border-[rgba(184,115,51,0.30)] bg-[rgba(184,115,51,0.12)] text-[#B87333]",
    green: "border-green-500/30 bg-green-500/10 text-green-300",
    red: "border-red-500/30 bg-red-500/10 text-red-300",
    blue: "border-blue-500/30 bg-blue-500/10 text-blue-300",
    zinc: "border-zinc-700 bg-zinc-800/70 text-zinc-200",
    amber: "border-amber-500/30 bg-amber-500/10 text-amber-300",
  };

  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-full border text-xs font-semibold ${
        tones[tone] || tones.zinc
      }`}
    >
      {text}
    </span>
  );
}

function StatCard({ label, value, subtext }) {
  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-4 shadow-sm">
      <div className="text-[11px] uppercase tracking-[0.12em] text-zinc-500">{label}</div>
      <div className="mt-2 text-2xl font-bold text-zinc-100">{value}</div>
      {subtext ? <div className="mt-1 text-xs text-zinc-500">{subtext}</div> : null}
    </div>
  );
}

function FilterSelect({ label, value, onChange, children }) {
  return (
    <div>
      <label className="block text-zinc-400 text-[11px] uppercase tracking-[0.12em] mb-1.5">
        {label}
      </label>
      <select
        className="w-full px-3 py-3 rounded-xl bg-zinc-950 border border-zinc-800 text-zinc-100 outline-none focus:border-[rgba(184,115,51,0.45)]"
        value={value}
        onChange={onChange}
      >
        {children}
      </select>
    </div>
  );
}

function VerificationDots({ emailVerified, phoneVerified }) {
  return (
    <div className="flex items-center gap-2">
      <span
        title={emailVerified ? "Email verified" : "Email unverified"}
        className={`inline-flex h-6 w-6 items-center justify-center rounded-full border text-[10px] font-bold ${
          emailVerified
            ? "border-green-500/30 bg-green-500/10 text-green-300"
            : "border-zinc-700 bg-zinc-800 text-zinc-400"
        }`}
      >
        E
      </span>
      <span
        title={phoneVerified ? "Phone verified" : "Phone unverified"}
        className={`inline-flex h-6 w-6 items-center justify-center rounded-full border text-[10px] font-bold ${
          phoneVerified
            ? "border-green-500/30 bg-green-500/10 text-green-300"
            : "border-zinc-700 bg-zinc-800 text-zinc-400"
        }`}
      >
        P
      </span>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-8 text-center">
      <div className="text-zinc-200 font-semibold">No users match your current filters.</div>
      <div className="text-sm text-zinc-500 mt-2">
        Try clearing or widening the filters above.
      </div>
    </div>
  );
}

export default function AdminUsers() {
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [latestEventMap, setLatestEventMap] = useState({});
  const [loadingEvents, setLoadingEvents] = useState(true);

  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [smsFilter, setSmsFilter] = useState("all");
  const [verificationFilter, setVerificationFilter] = useState("all");

  const [backfilling, setBackfilling] = useState(false);
  const [sendingToUserId, setSendingToUserId] = useState("");
  const [actionUserId, setActionUserId] = useState("");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const sendCustomPasswordSetupEmail = httpsCallable(
    functions,
    "sendCustomPasswordSetupEmail"
  );

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const qRef = query(collection(db, "users"));
        const snap = await getDocs(qRef);

        const data = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        data.sort((a, b) => {
          const aMs =
            (typeof getJoinedAt(a)?.toMillis === "function" && getJoinedAt(a).toMillis()) ||
            (getJoinedAt(a)?.seconds ? getJoinedAt(a).seconds * 1000 : 0);
          const bMs =
            (typeof getJoinedAt(b)?.toMillis === "function" && getJoinedAt(b).toMillis()) ||
            (getJoinedAt(b)?.seconds ? getJoinedAt(b).seconds * 1000 : 0);

          return bMs - aMs;
        });

        setUsers(data);
      } catch (err) {
        console.error("Failed loading users:", err);
      }

      setLoading(false);
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    const fetchLatestEvents = async () => {
      setLoadingEvents(true);

      try {
        const tournamentsSnap = await getDocs(collection(db, "tournaments"));

        const nextMap = {};

        tournamentsSnap.docs.forEach((docSnap) => {
          const tournament = { id: docSnap.id, ...docSnap.data() };
          const entrants = tournament?.entrantsCount;

          void entrants;
        });

        for (const tournamentDoc of tournamentsSnap.docs) {
          const tournament = {
            id: tournamentDoc.id,
            ...tournamentDoc.data(),
          };

          const entrantsSnap = await getDocs(
            collection(db, "tournaments", tournament.id, "entrants")
          );

          entrantsSnap.docs.forEach((entrantDoc) => {
            const entrant = entrantDoc.data() || {};
            const memberUid = entrantDoc.id;

            const candidateAt =
              entrant.eliminatedAt ||
              entrant.updatedAt ||
              entrant.registeredViaSmsAt ||
              entrant.createdAt ||
              tournament.date ||
              tournament.createdAt ||
              null;

            const existing = nextMap[memberUid];
            const existingMs = existing ? getTimestampMs(existing.at) : 0;
            const candidateMs = getTimestampMs(candidateAt);

            if (!existing || candidateMs > existingMs) {
              nextMap[memberUid] = {
                at: candidateAt,
                tournamentId: tournament.id,
                tournamentName: tournament.name || "Tournament",
                status: buildTournamentHistoryStatus(entrant),
              };
            }
          });
        }

        setLatestEventMap(nextMap);
      } catch (err) {
        console.error("Failed loading latest events:", err);
      } finally {
        setLoadingEvents(false);
      }
    };

    fetchLatestEvents();
  }, []);

  const adminCount = useMemo(() => {
    return users.filter((u) => u.role === "admin").length;
  }, [users]);

  const totalActive = useMemo(() => {
    return users.filter((u) => !isDisabled(u)).length;
  }, [users]);

  const totalDisabled = useMemo(() => {
    return users.filter((u) => isDisabled(u)).length;
  }, [users]);

  const totalSmsOptIn = useMemo(() => {
    return users.filter((u) => u.smsOptIn === true).length;
  }, [users]);

  const totalEmailVerified = useMemo(() => {
    return users.filter((u) => isEmailVerified(u)).length;
  }, [users]);

  const totalPhoneVerified = useMemo(() => {
    return users.filter((u) => isPhoneVerified(u)).length;
  }, [users]);

  const filteredUsers = useMemo(() => {
    const q = normLower(search);

    return users.filter((u) => {
      const name = normLower(u.name || u.displayName || "");
      const email = normLower(u.email || "");
      const phone = normLower(u.phone || "");
      const status = isDisabled(u) ? "disabled" : "active";
      const role = normLower(u.role || "member");
      const lastEventName = normLower(latestEventMap[u.id]?.tournamentName || "");
      const lastEventStatus = normLower(latestEventMap[u.id]?.status || "");

      const matchesSearch =
        !q ||
        name.includes(q) ||
        email.includes(q) ||
        phone.includes(q) ||
        status.includes(q) ||
        role.includes(q) ||
        lastEventName.includes(q) ||
        lastEventStatus.includes(q);

      const matchesRole =
        roleFilter === "all" ? true : (u.role || "member") === roleFilter;

      const matchesStatus =
        statusFilter === "all"
          ? true
          : statusFilter === "active"
          ? !isDisabled(u)
          : isDisabled(u);

      const matchesSms =
        smsFilter === "all"
          ? true
          : smsFilter === "opted_in"
          ? u.smsOptIn === true
          : u.smsOptIn !== true;

      const matchesVerification =
        verificationFilter === "all"
          ? true
          : verificationFilter === "email_verified"
          ? isEmailVerified(u)
          : verificationFilter === "email_unverified"
          ? !isEmailVerified(u)
          : verificationFilter === "phone_verified"
          ? isPhoneVerified(u)
          : !isPhoneVerified(u);

      return (
        matchesSearch &&
        matchesRole &&
        matchesStatus &&
        matchesSms &&
        matchesVerification
      );
    });
  }, [users, search, roleFilter, statusFilter, smsFilter, verificationFilter, latestEventMap]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (roleFilter !== "all") count += 1;
    if (statusFilter !== "all") count += 1;
    if (smsFilter !== "all") count += 1;
    if (verificationFilter !== "all") count += 1;
    if (search.trim()) count += 1;
    return count;
  }, [roleFilter, statusFilter, smsFilter, verificationFilter, search]);

  const clearFilters = () => {
    setSearch("");
    setRoleFilter("all");
    setStatusFilter("all");
    setSmsFilter("all");
    setVerificationFilter("all");
  };

  const toggleAdmin = async (user, e) => {
    e?.stopPropagation();

    const isCurrentlyAdmin = user.role === "admin";

    if (isCurrentlyAdmin && adminCount <= 1) {
      return alert("You can’t remove the last admin.");
    }

    const newRole = isCurrentlyAdmin ? "member" : "admin";
    setActionUserId(user.id);

    try {
      const ref = doc(db, "users", user.id);
      const fields = computeSearchFields(user);

      await updateDoc(ref, {
        role: newRole,
        ...fields,
      });

      setUsers((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, role: newRole, ...fields } : u))
      );
    } catch (err) {
      console.error("Role update failed:", err);
      alert("Could not update role.");
    } finally {
      setActionUserId("");
    }
  };

  const toggleStatus = async (user, e) => {
    e?.stopPropagation();

    const nextDisabled = !isDisabled(user);
    const newStatus = nextDisabled ? "disabled" : "active";
    setActionUserId(user.id);

    try {
      const ref = doc(db, "users", user.id);
      const fields = computeSearchFields(user);

      await updateDoc(ref, {
        status: newStatus,
        ...fields,
      });

      setUsers((prev) =>
        prev.map((u) =>
          u.id === user.id ? { ...u, status: newStatus, ...fields } : u
        )
      );
    } catch (err) {
      console.error("Status update failed:", err);
      alert("Could not update status.");
    } finally {
      setActionUserId("");
    }
  };

  const backfillSearchFields = async () => {
    const confirmRun = window.confirm(
      "This will write nameLower/emailLower/searchName for all users. Continue?"
    );
    if (!confirmRun) return;

    setBackfilling(true);

    try {
      const snap = await getDocs(collection(db, "users"));
      const batch = writeBatch(db);

      let count = 0;

      snap.docs.forEach((d) => {
        const data = d.data() || {};
        const ref = doc(db, "users", d.id);

        const name = data.name || data.displayName || "";
        const email = data.email || "";

        const next = {
          nameLower: normLower(name || email),
          emailLower: normLower(email),
          searchName: String(name || email || "").trim(),
        };

        batch.set(ref, next, { merge: true });
        count += 1;
      });

      await batch.commit();

      setUsers((prev) =>
        prev.map((u) => ({
          ...u,
          ...computeSearchFields(u),
        }))
      );

      alert(`Backfill complete: updated ${count} user records.`);
    } catch (e) {
      console.error("Backfill failed:", e);
      alert("Backfill failed. Check console.");
    } finally {
      setBackfilling(false);
    }
  };

  const sendSetupEmail = async (user, e) => {
    e?.stopPropagation();

    const email = String(user?.email || "").trim();
    if (!email) return alert("User has no email.");

    const confirmSend = window.confirm(
      `Email temporary password to ${email}?`
    );
    if (!confirmSend) return;

    setSendingToUserId(user.id);

    try {
      await sendCustomPasswordSetupEmail({ email: email.toLowerCase() });
      alert("Temporary password email sent.");
    } catch (err) {
      console.error("Setup email failed:", err);
      alert("Failed to send temporary password email.");
    } finally {
      setSendingToUserId("");
    }
  };

  const openUserDetails = (userId) => {
    navigate(`/admin/users/${userId}`);
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div>
            <h2 className="text-3xl font-bold text-[#B87333]">Admin Users</h2>
            <p className="text-zinc-400 mt-1 text-sm">
              Manage members, admins, communication status, and account access.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={backfillSearchFields}
              disabled={backfilling}
              className={`px-4 py-3 rounded-xl font-semibold text-sm ${
                backfilling
                  ? "bg-zinc-700 text-zinc-300 cursor-not-allowed"
                  : "bg-[#B87333] hover:bg-[#A46230] text-zinc-950"
              }`}
            >
              {backfilling ? "Backfilling..." : "Backfill Search Fields"}
            </button>

            <button
              onClick={() => navigate("/pending-applications")}
              className="px-4 py-3 rounded-xl font-semibold text-sm bg-zinc-800 hover:bg-zinc-700 text-zinc-100"
            >
              Pending Applications
            </button>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
          <StatCard label="Total Users" value={users.length} />
          <StatCard label="Admins" value={adminCount} />
          <StatCard label="Active Users" value={totalActive} />
          <StatCard label="Disabled" value={totalDisabled} />
          <StatCard label="SMS Opt-In" value={totalSmsOptIn} />
          <StatCard
            label="Verified"
            value={`${totalEmailVerified}/${totalPhoneVerified}`}
            subtext="email / phone"
          />
        </div>

        <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-4 sm:p-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="w-full lg:max-w-md">
              <label className="block text-zinc-400 text-[11px] uppercase tracking-[0.12em] mb-1.5">
                Search
              </label>
              <input
                className="w-full px-3 py-3 rounded-xl bg-zinc-950 border border-zinc-800 text-zinc-100 outline-none focus:border-[rgba(184,115,51,0.45)]"
                placeholder="Search name / email / phone / role / status / event..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setMobileFiltersOpen((prev) => !prev)}
                className="lg:hidden px-4 py-3 rounded-xl font-semibold text-sm bg-zinc-800 hover:bg-zinc-700 text-zinc-100"
              >
                {mobileFiltersOpen ? "Hide Filters" : `Filters${activeFilterCount ? ` (${activeFilterCount})` : ""}`}
              </button>

              <button
                onClick={clearFilters}
                className="px-4 py-3 rounded-xl font-semibold text-sm bg-zinc-800 hover:bg-zinc-700 text-zinc-100"
              >
                Clear Filters
              </button>
            </div>
          </div>

          <div className={`${mobileFiltersOpen ? "grid" : "hidden"} lg:grid gap-3 md:grid-cols-2 xl:grid-cols-4 mt-4`}>
            <FilterSelect
              label="Role"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="all">All roles</option>
              <option value="admin">Admins</option>
              <option value="member">Members</option>
            </FilterSelect>

            <FilterSelect
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="disabled">Disabled</option>
            </FilterSelect>

            <FilterSelect
              label="SMS"
              value={smsFilter}
              onChange={(e) => setSmsFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="opted_in">SMS opted in</option>
              <option value="not_opted_in">SMS not opted in</option>
            </FilterSelect>

            <FilterSelect
              label="Verification"
              value={verificationFilter}
              onChange={(e) => setVerificationFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="email_verified">Email verified</option>
              <option value="email_unverified">Email unverified</option>
              <option value="phone_verified">Phone verified</option>
              <option value="phone_unverified">Phone unverified</option>
            </FilterSelect>
          </div>
        </div>

        <div className="flex items-center justify-between text-sm text-zinc-500">
          <div>
            Showing <span className="text-zinc-200 font-semibold">{filteredUsers.length}</span> of{" "}
            <span className="text-zinc-200 font-semibold">{users.length}</span> users
          </div>
          {activeFilterCount > 0 ? (
            <div>{activeFilterCount} filter{activeFilterCount === 1 ? "" : "s"} active</div>
          ) : null}
        </div>

        {loading ? (
          <p className="text-zinc-300">Loading users...</p>
        ) : filteredUsers.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <div className="hidden xl:block rounded-2xl border border-zinc-800 bg-zinc-900 overflow-hidden">
              <div className="grid grid-cols-[minmax(260px,2fr)_110px_110px_130px_180px_120px_90px_280px] gap-3 px-5 py-4 border-b border-zinc-800 text-[11px] uppercase tracking-[0.12em] text-zinc-500">
                <div>User</div>
                <div>Role</div>
                <div>Status</div>
                <div>Joined</div>
                <div>Last Event</div>
                <div>Verified</div>
                <div>SMS</div>
                <div>Actions</div>
              </div>

              <div className="divide-y divide-zinc-800">
                {filteredUsers.map((user) => {
                  const userIsAdmin = user.role === "admin";
                  const onlyAdmin = userIsAdmin && adminCount <= 1;
                  const disabled = isDisabled(user);
                  const rowBusy = actionUserId === user.id;
                  const joinedAtText = formatDateShort(getJoinedAt(user));
                  const latestEvent = latestEventMap[user.id];

                  return (
                    <div
                      key={user.id}
                      onClick={() => openUserDetails(user.id)}
                      className="grid grid-cols-[minmax(260px,2fr)_110px_110px_130px_180px_120px_90px_280px] gap-3 px-5 py-4 cursor-pointer hover:bg-zinc-950/40"
                    >
                      <div className="min-w-0 flex items-center gap-3">
                        <Avatar user={user} size={46} />
                        <div className="min-w-0">
                          <div className="text-zinc-100 font-semibold truncate">
                            {user.name || user.displayName || "No name"}
                          </div>
                          <div className="text-sm text-zinc-500 truncate">{user.email || "—"}</div>
                          <div className="text-xs text-zinc-600 truncate">{user.phone || "No phone"}</div>
                        </div>
                      </div>

                      <div className="flex items-center">
                        <Badge text={userIsAdmin ? "Admin" : "Member"} tone={userIsAdmin ? "copper" : "zinc"} />
                      </div>

                      <div className="flex items-center">
                        <Badge text={disabled ? "Disabled" : "Active"} tone={disabled ? "red" : "green"} />
                      </div>

                      <div className="flex items-center text-sm text-zinc-300">{joinedAtText}</div>

                      <div className="flex items-center">
                        {loadingEvents ? (
                          <div className="text-sm text-zinc-500">Loading...</div>
                        ) : latestEvent ? (
                          <div className="min-w-0">
                            <div className="text-sm text-zinc-200 truncate">
                              {formatDateShort(latestEvent.at)}
                            </div>
                            <div className="text-xs text-zinc-500 truncate">
                              {latestEvent.tournamentName}
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm text-zinc-500">—</div>
                        )}
                      </div>

                      <div className="flex items-center">
                        <VerificationDots
                          emailVerified={isEmailVerified(user)}
                          phoneVerified={isPhoneVerified(user)}
                        />
                      </div>

                      <div className="flex items-center text-sm">
                        <span className={user.smsOptIn ? "text-green-300" : "text-zinc-500"}>
                          {user.smsOptIn ? "Yes" : "No"}
                        </span>
                      </div>

                      <div
                        className="flex items-center gap-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          onClick={() => openUserDetails(user.id)}
                          className="px-3 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-xs font-semibold"
                        >
                          View
                        </button>

                        <button
                          type="button"
                          onClick={(e) => sendSetupEmail(user, e)}
                          disabled={sendingToUserId === user.id}
                          className={`px-3 py-2 rounded-lg text-xs font-semibold ${
                            sendingToUserId === user.id
                              ? "bg-zinc-700 text-zinc-300 cursor-not-allowed"
                              : "bg-zinc-800 hover:bg-zinc-700 text-zinc-100"
                          }`}
                        >
                          {sendingToUserId === user.id ? "Sending..." : "Email Temp"}
                        </button>

                        <button
                          type="button"
                          onClick={(e) => toggleStatus(user, e)}
                          disabled={rowBusy}
                          className={`px-3 py-2 rounded-lg text-xs font-semibold ${
                            disabled
                              ? "bg-green-700 hover:bg-green-800 text-white"
                              : "bg-red-600 hover:bg-red-700 text-white"
                          } ${rowBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                        >
                          {disabled ? "Activate" : "Disable"}
                        </button>

                        <button
                          type="button"
                          onClick={(e) => toggleAdmin(user, e)}
                          disabled={rowBusy || onlyAdmin}
                          title={onlyAdmin ? "You can’t remove the last admin." : ""}
                          className={`px-3 py-2 rounded-lg text-xs font-semibold ${
                            userIsAdmin
                              ? "bg-[rgba(184,115,51,0.18)] text-[#B87333] hover:bg-[rgba(184,115,51,0.24)]"
                              : "bg-[#B87333] hover:bg-[#A46230] text-zinc-950"
                          } ${(rowBusy || onlyAdmin) ? "opacity-60 cursor-not-allowed" : ""}`}
                        >
                          {userIsAdmin ? "Remove Admin" : "Make Admin"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="xl:hidden space-y-4">
              {filteredUsers.map((user) => {
                const userIsAdmin = user.role === "admin";
                const onlyAdmin = userIsAdmin && adminCount <= 1;
                const disabled = isDisabled(user);
                const rowBusy = actionUserId === user.id;
                const latestEvent = latestEventMap[user.id];

                return (
                  <div
                    key={user.id}
                    onClick={() => openUserDetails(user.id)}
                    className="rounded-2xl border border-zinc-800 bg-zinc-900 p-4 cursor-pointer hover:border-[rgba(184,115,51,0.28)]"
                  >
                    <div className="flex items-start gap-3">
                      <Avatar user={user} size={52} />

                      <div className="min-w-0 flex-1">
                        <div className="text-zinc-100 font-semibold text-lg truncate">
                          {user.name || user.displayName || "No name"}
                        </div>
                        <div className="text-sm text-zinc-400 truncate">{user.email || "—"}</div>
                        <div className="text-xs text-zinc-500 mt-1">{user.phone || "No phone on file"}</div>

                        <div className="mt-3 flex flex-wrap gap-2">
                          <Badge text={userIsAdmin ? "Admin" : "Member"} tone={userIsAdmin ? "copper" : "zinc"} />
                          <Badge text={disabled ? "Disabled" : "Active"} tone={disabled ? "red" : "green"} />
                          {user.smsOptIn ? (
                            <Badge text="SMS Opted In" tone="blue" />
                          ) : (
                            <Badge text="SMS Off" tone="zinc" />
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 mt-4">
                      <div className="rounded-xl border border-zinc-800 bg-zinc-950 px-3 py-3">
                        <div className="text-[11px] uppercase tracking-[0.12em] text-zinc-500">Joined</div>
                        <div className="text-sm text-zinc-200 font-semibold mt-1">
                          {formatDateShort(getJoinedAt(user))}
                        </div>
                      </div>

                      <div className="rounded-xl border border-zinc-800 bg-zinc-950 px-3 py-3">
                        <div className="text-[11px] uppercase tracking-[0.12em] text-zinc-500">Last Event</div>
                        <div className="text-sm text-zinc-200 font-semibold mt-1">
                          {loadingEvents
                            ? "Loading..."
                            : latestEvent
                            ? formatDateShort(latestEvent.at)
                            : "—"}
                        </div>
                        <div className="text-[11px] text-zinc-500 mt-1 truncate">
                          {latestEvent?.tournamentName || "No event yet"}
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 flex items-center justify-between gap-3">
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.12em] text-zinc-500 mb-2">
                          Verification
                        </div>
                        <VerificationDots
                          emailVerified={isEmailVerified(user)}
                          phoneVerified={isPhoneVerified(user)}
                        />
                      </div>

                      <div className="text-right">
                        <div className="text-[11px] uppercase tracking-[0.12em] text-zinc-500 mb-2">
                          SMS
                        </div>
                        <div className={user.smsOptIn ? "text-green-300 font-semibold" : "text-zinc-500 font-semibold"}>
                          {user.smsOptIn ? "Yes" : "No"}
                        </div>
                      </div>
                    </div>

                    <div
                      className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        onClick={() => openUserDetails(user.id)}
                        className="px-3 py-3 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-sm font-semibold"
                      >
                        View
                      </button>

                      <button
                        type="button"
                        onClick={(e) => sendSetupEmail(user, e)}
                        disabled={sendingToUserId === user.id}
                        className={`px-3 py-3 rounded-xl text-sm font-semibold ${
                          sendingToUserId === user.id
                            ? "bg-zinc-700 text-zinc-300 cursor-not-allowed"
                            : "bg-zinc-800 hover:bg-zinc-700 text-zinc-100"
                        }`}
                      >
                        {sendingToUserId === user.id ? "Sending..." : "Email Temp"}
                      </button>

                      <button
                        type="button"
                        onClick={(e) => toggleStatus(user, e)}
                        disabled={rowBusy}
                        className={`px-3 py-3 rounded-xl text-sm font-semibold ${
                          disabled
                            ? "bg-green-700 hover:bg-green-800 text-white"
                            : "bg-red-600 hover:bg-red-700 text-white"
                        } ${rowBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                      >
                        {disabled ? "Activate" : "Disable"}
                      </button>

                      <button
                        type="button"
                        onClick={(e) => toggleAdmin(user, e)}
                        disabled={rowBusy || onlyAdmin}
                        title={onlyAdmin ? "You can’t remove the last admin." : ""}
                        className={`px-3 py-3 rounded-xl text-sm font-semibold ${
                          userIsAdmin
                            ? "bg-[rgba(184,115,51,0.18)] text-[#B87333] hover:bg-[rgba(184,115,51,0.24)]"
                            : "bg-[#B87333] hover:bg-[#A46230] text-zinc-950"
                        } ${(rowBusy || onlyAdmin) ? "opacity-60 cursor-not-allowed" : ""}`}
                      >
                        {userIsAdmin ? "Remove Admin" : "Make Admin"}
                      </button>
                    </div>

                    <div className="mt-3 text-right text-xs text-zinc-500">
                      Tap card for full details →
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}